import React, { useEffect, useState } from 'react'
import {
	Alert, Box, Divider, MenuItem, Table, TableBody, TableCell, TableHead,
	TableRow, TextField, Typography,
} from '@mui/material'

import { IContractUsage, formatDay, formatEur, formatGb, formatHits, getContractUsage } from '../../../Services/contractDates'

interface Props {
	billingAccountId: string
}

// La consommation d'un compte sous contrat, sur son année contractuelle.
//
// ⚠️ REMPLACE le rapport de dépassements mensuel qui occupait cette place. Ce
// dernier appliquait l'inclus du forfait Pay as you go (10 €) à un compte qui
// n'en a pas, sur un mois calendaire qui n'est pas sa période : il annonçait un
// « dépassement » calculé sur une règle qui ne le concerne pas.
//
// Un compte sous contrat n'a ni inclus ni dépassement : on montre sa DÉPENSE, et
// le commercial négocie l'ajustement au vu de ces chiffres.

// #region ContractUsageBlock
export const ContractUsageBlock: React.FC<Props> = ({ billingAccountId }) => {
	const [usage, setUsage] = useState<IContractUsage | null>(null)
	const [period, setPeriod] = useState('')

	useEffect(() => {
		if (!billingAccountId) return
		getContractUsage(billingAccountId, period)
			.then(setUsage)
			.catch(() => setUsage(null))
	}, [billingAccountId, period])

	if (!usage) return null

	if (usage.periods.length === 0) {
		return (
			<>
				<Divider sx={{ my: 3 }} />
				<Typography variant="subtitle1" gutterBottom>
					Consommation de l’année
				</Typography>
				{/* Sans date de début, aucune année ne se découpe : le dire, plutôt que
				    d'afficher un tableau vide qui se lirait comme « rien consommé ». */}
				<Alert severity="info">
					Aucune date de contrat saisie : la consommation n’est rattachée à aucune période. Renseigne
					la date de début dans le bloc « Statut du contrat ».
				</Alert>
			</>
		)
	}

	return (
		<>
			<Divider sx={{ my: 3 }} />

			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2 }}>
				<Box>
					<Typography variant="subtitle1">Consommation de l’année</Typography>
					<Typography variant="caption" color="text.secondary">
						{formatDay(usage.periodStart)} → {formatDay(usage.periodLastDay)}
						{usage.totalCostEur !== undefined && <> · coût Google&nbsp;: <strong>{formatEur(usage.totalCostEur)}</strong></>}
					</Typography>
				</Box>
				<TextField
					select
					size="small"
					label="Année"
					value={usage.period}
					onChange={(e) => setPeriod(e.target.value)}
					sx={{ minWidth: 200 }}
				>
					{usage.periods.map((p) => (
						<MenuItem key={p} value={p}>
							à partir du {formatDay(p)}
						</MenuItem>
					))}
				</TextField>
			</Box>

			{/* ⚠️ Une année en cours ou raccourcie ne se compare pas à une année
			    pleine. Sans le dire, un volume plus faible se lit comme une baisse
			    d'usage alors que la période est simplement plus courte. */}
			{usage.ongoing && (
				<Alert severity="info" sx={{ mb: 1.5 }}>
					Année en cours ({usage.periodDays} jours) : volumes partiels, non comparables à une année complète.
				</Alert>
			)}

			{usage.projects.length > 0 && (
				<Table size="small" sx={{ mb: 2 }}>
					<TableHead>
						<TableRow>
							<TableCell>Projet managé</TableCell>
							<TableCell align="right">Analysé (cumul)</TableCell>
							{/* ⚠️ MOYENNE : le stockage est un stock facturé en Go-mois.
							    Cumulé sur douze mois, 400 Go s'afficheraient « 4 800 ». */}
							<TableCell align="right">Stocké (moyenne)</TableCell>
							<TableCell>Services Google</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{usage.projects.map((p) => (
							<TableRow key={p.folderId}>
								<TableCell>
									{p.name}
									{/* ⚠️ Un écart de journées dit « pas mesuré », jamais « rien
									    consommé » — sur une année, la nuance vaut une facture. */}
									{p.measuredDays < usage.periodDays && (
										<Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
											{p.measuredDays} jour(s) mesuré(s) sur {usage.periodDays}
										</Typography>
									)}
								</TableCell>
								<TableCell align="right">{formatGb(p.analysisGb)}</TableCell>
								<TableCell align="right">{formatGb(p.storageAvgGb)}</TableCell>
								<TableCell>
									{p.services.length === 0 && <Typography variant="caption" color="text.secondary">—</Typography>}
									{p.services.map((s) => (
										<Typography key={s.service} variant="caption" sx={{ display: 'block' }}>
											{s.service}
											{s.costEur !== undefined && <> — {formatEur(s.costEur)}</>}
										</Typography>
									))}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}

			{usage.servers.length > 0 && (
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Serveur Track</TableCell>
							<TableCell align="right">Hits (cumul)</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{usage.servers.map((s) => (
							<TableRow key={s.id}>
								<TableCell>{s.domain}</TableCell>
								{/* ⚠️ « non mesuré » et surtout pas « 0 » : un serveur
								    provisionné avant le sidecar ne remonte aucun hit, et zéro
								    le ferait passer pour un serveur sans trafic. */}
								<TableCell align="right">
									{s.hits === null ? (
										<Typography variant="caption" color="warning.main">non mesuré</Typography>
									) : (
										formatHits(s.hits)
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}

			{usage.projects.length === 0 && usage.servers.length === 0 && (
				<Typography variant="caption" color="text.secondary">
					Aucun projet managé ni serveur Track sur ce compte.
				</Typography>
			)}

			{/* ⚠️ La phrase qui évite un contresens : ces montants ne sont pas une
			    facture, et rien ne sera posé automatiquement. */}
			<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
				Montants = ce que Quanti paie à Google. Aucun dépassement n’est calculé pour un compte sous
				contrat : l’ajustement se négocie au vu de ces volumes.
			</Typography>
		</>
	)
}

// #endregion
